import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import Loading from '../Loading';
import Error from '../Error';
import FillInPanel from './FillInPanel';
import FillInResults from './FillInResults';
import ExerciseLayout from '../shared/ExerciseLayout';
import { fetchConversationFillInExercise, saveExerciseProgress, loadExerciseProgress, resetExerciseProgress, recordExerciseAttempt, getConversationFillinExplanation } from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';
import { useAuth } from '../../contexts/AuthContext';

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const ConversationFillInExercise = forwardRef(({ topic = "groceries", onBack }, ref) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [wordOptions, setWordOptions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [droppedWord, setDroppedWord] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'correct', 'incorrect', null
  const [score, setScore] = useState(0);
  const [userResponses, setUserResponses] = useState({});
  const [revealed, setRevealed] = useState(new Set());
  const [showResults, setShowResults] = useState(false);
  const [engagementData, setEngagementData] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [explanationLoading, setExplanationLoading] = useState(false);
  const { stop: stopTimer } = useExerciseTimer();

  const isIndependent = user?.support_level === 'independent';

  const persist = useCallback((overrides = {}) => {
    return saveExerciseProgress(topic, 'conversation_fillin', {
      currentQuestionIndex,
      userResponses,
      score,
      feedback,
      droppedWord,
      revealed: [...revealed],
      showResults,
      ...overrides,
    }).catch(err => console.error('Error saving progress:', err));
  }, [topic, currentQuestionIndex, userResponses, score, feedback, droppedWord, revealed, showResults]);

  // Expose saveProgress method to parent component
  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      await persist();
    }
  }));

  // Fetch questions from API on component mount
  const fetchExercises = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchConversationFillInExercise({ topic });

      setQuestions(data.questions);
      setWordOptions(shuffleArray(data.word_options));
    } catch (err) {
      console.error('Error fetching conversation fill-in exercise:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [topic]);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  // Load saved progress after questions are loaded
  useEffect(() => {
    const loadProgress = async () => {
      if (!loading && questions.length > 0) {
        try {
          const savedProgress = await loadExerciseProgress(topic, 'conversation_fillin');
          const pd = savedProgress?.progress_data;
          if (pd) {
            if (pd.currentQuestionIndex !== undefined) setCurrentQuestionIndex(pd.currentQuestionIndex);
            if (pd.userResponses) setUserResponses(pd.userResponses);
            if (pd.score !== undefined) setScore(pd.score);
            if (pd.feedback !== undefined) setFeedback(pd.feedback);
            if (pd.droppedWord !== undefined) setDroppedWord(pd.droppedWord);
            if (pd.revealed) setRevealed(new Set(pd.revealed));
            if (pd.showResults) setShowResults(true);
          }
        } catch (err) {
          console.error('Error loading progress:', err);
        }
      }
    };

    loadProgress();
  }, [loading, questions.length, topic]);

  const currentQuestion = questions[currentQuestionIndex];

  const loadExplanation = (question, wrongAnswer = null) => {
    setExplanation(null);
    setExplanationLoading(true);
    getConversationFillinExplanation(question.full_answer, question.answer, wrongAnswer)
      .then(result => setExplanation(result?.explanation || null))
      .catch(err => {
        console.error('Error loading fill-in explanation:', err);
        setExplanation(null);
      })
      .finally(() => setExplanationLoading(false));
  };

  const handleWordClick = async (word) => {
    // Ignore clicks if already answered
    if (feedback !== null) return;

    const isCorrect = word === currentQuestion.answer;
    const newScore = isCorrect ? score + 1 : score;
    const newUserResponses = {
      ...userResponses,
      [currentQuestion.id]: { answer: word, correct: isCorrect }
    };

    setDroppedWord(word);
    setUserResponses(newUserResponses);
    setFeedback(isCorrect ? 'correct' : 'incorrect');
    if (isCorrect) {
      setScore(newScore);
    } else {
      loadExplanation(currentQuestion, word);
    }

    await persist({
      userResponses: newUserResponses,
      score: newScore,
      feedback: isCorrect ? 'correct' : 'incorrect',
      droppedWord: word,
    });
  };

  const handleReveal = async () => {
    const correctAnswer = currentQuestion.answer;
    const newRevealed = new Set([...revealed, currentQuestion.id]);
    const newUserResponses = {
      ...userResponses,
      [currentQuestion.id]: { answer: correctAnswer, correct: false, seen: true }
    };

    setRevealed(newRevealed);
    setDroppedWord(correctAnswer);
    setFeedback('incorrect');
    setUserResponses(newUserResponses);
    loadExplanation(currentQuestion);

    await persist({
      userResponses: newUserResponses,
      feedback: 'incorrect',
      droppedWord: correctAnswer,
      revealed: [...newRevealed],
    });
  };

  const finish = async (finalResponses) => {
    const finalTime = stopTimer();

    // Calculate errors from responses (exclude revealed "seen" items)
    const errors = [];
    Object.entries(finalResponses).forEach(([qid, response]) => {
      if (!response.correct && !response.seen) {
        const question = questions.find(q => String(q.id) === String(qid));
        errors.push({
          exercise_id: Number(qid),
          user_answer: response.answer || '',
          correct_answer: question?.answer
        });
      }
    });

    setShowResults(true);

    // Record attempt in the background (fast - no LLM)
    setEngagementLoading(true);
    recordExerciseAttempt({
      topic,
      exercise_type: 'conversation_fillin',
      score,
      max_score: questions.length,
      time_spent_seconds: finalTime,
      errors: errors.length > 0 ? errors : null,
      exercise_ids: questions.map(q => q.id)
    })
      .then(result => {
        setEngagementData(result);
        setEngagementLoading(false);
      })
      .catch(trackingError => {
        console.error('Failed to record exercise attempt:', trackingError);
        setEngagementLoading(false);
      });

    await persist({
      showResults: true,
      userResponses: finalResponses,
      errors: errors.length > 0 ? errors : null,
    });
  };

  const handleNext = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setDroppedWord(null);
      setFeedback(null);
      setExplanation(null);
      setExplanationLoading(false);
      await persist({ currentQuestionIndex: nextIndex, droppedWord: null, feedback: null });
    } else {
      await finish(userResponses);
    }
  };

  const restart = async () => {
    setCurrentQuestionIndex(0);
    setDroppedWord(null);
    setFeedback(null);
    setScore(0);
    setUserResponses({});
    setRevealed(new Set());
    setShowResults(false);
    setEngagementData(null);
    setEngagementLoading(false);
    setExplanation(null);
    setExplanationLoading(false);

    try {
      await resetExerciseProgress(topic, 'conversation_fillin');
    } catch (err) {
      console.error('Error resetting progress:', err);
    }

    await fetchExercises();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  // Loading state
  if (loading) {
    return <Loading />;
  }

  // Error state
  if (error) {
    return <Error error={error} />;
  }

  // No questions state
  if (!questions || questions.length === 0) {
    return (
      <ExerciseLayout title="Doplňování" color="blue" onBack={handleBack}>
        <div className="text-center py-8 text-gray-500 text-lg">
          Žádná cvičení nejsou k dispozici
        </div>
      </ExerciseLayout>
    );
  }

  // Results view
  if (showResults) {
    // Derive userAnswers for FillInResults (revealed "seen" items count as unanswered)
    const userAnswers = {};
    questions.forEach(q => {
      const resp = userResponses[q.id];
      userAnswers[q.id] = resp && !resp.seen ? resp.answer : '';
    });

    return (
      <FillInResults
        questions={questions}
        userAnswers={userAnswers}
        restart={restart}
        onBack={handleBack}
        topic={topic}
        engagementData={engagementData}
        engagementLoading={engagementLoading}
      />
    );
  }

  return (
    <ExerciseLayout
      title="Doplňování"
      subtitle="Doplňte správné odpovědi"
      color="blue"
      icon="💬"
      progress={{ current: currentQuestionIndex + 1, total: questions.length }}
      onBack={handleBack}
    >
      <FillInPanel
        question={currentQuestion}
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={questions.length}
        wordOptions={wordOptions}
        droppedWord={droppedWord}
        feedback={feedback}
        score={score}
        isRevealed={revealed.has(currentQuestion.id)}
        showRevealButton={!isIndependent}
        explanation={explanation}
        explanationLoading={explanationLoading}
        onWordClick={handleWordClick}
        onReveal={handleReveal}
        onNext={handleNext}
        topic={topic}
      />
    </ExerciseLayout>
  );
});

export default ConversationFillInExercise;
